// src/components/Onboarding/SearchableMultiSelect.tsx
import { useState } from "react";
import { ChevronsUpDown, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface SearchableMultiSelectProps {
  label: string;
  placeholder?: string;
  values: string[];
  onChange: (values: string[]) => void;
  options: string[];
  required?: boolean;
  error?: string;
}

export function SearchableMultiSelect({
  label,
  placeholder,
  values,
  onChange,
  options,
  required,
  error,
}: SearchableMultiSelectProps) {
  const [open, setOpen] = useState(false);

  const toggleValue = (val: string) => {
    if (values.includes(val)) {
      onChange(values.filter((v) => v !== val));
    } else {
      onChange([...values, val]);
    }
  };

  const clearValue = (val: string) => {
    onChange(values.filter((v) => v !== val));
  };

  const selectedLabel = values.length === 0 ? (placeholder || "Select...") : "";

  return (
    <div className="space-y-2 w-full">
      <Label className="text-sm font-medium">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </Label>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex min-h-[3rem] w-full items-center rounded-xl border-2 border-input bg-background px-3 py-2 text-sm text-left",
              "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            )}
          >
            <div className="flex flex-wrap gap-1 flex-1">
              {values.map((val) => (
                <Badge
                  key={val}
                  variant="default"
                  className="inline-flex items-center rounded-full border-transparent px-2.5 py-0.5 text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/80"
                >
                  <span className="max-w-[140px] truncate">{val}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearValue(val);
                    }}
                    className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </button>
                </Badge>
              ))}

              {selectedLabel && (
                <span className="text-muted-foreground text-sm">{selectedLabel}</span>
              )}
            </div>

            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>

        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command>
            <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                <ScrollArea className="max-h-64">
                  {options.map((option) => {
                    const isSelected = values.includes(option);
                    return (
                      <CommandItem
                        key={option}
                        value={option}
                        onSelect={() => toggleValue(option)}
                        className={cn(isSelected && "bg-accent")}
                      >
                        <span className="truncate">{option}</span>
                      </CommandItem>
                    );
                  })}
                </ScrollArea>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
